import alt from 'components/Dispatcher';
import RepProfileSource from 'sources/RepProfileSource';
import generateAsyncActions from 'utils/generateAsyncActions';
import {Api} from 'sources/api';
import CoachingPlanSource from 'sources/coaching/CoachingPlanSource';
import DirectReportsSource from 'sources/coaching/DirectReportsSource';
import moment from 'moment';
import {Record} from 'immutable';
import {MetricType} from 'sources/dashboard/DashMetricsSource';

export const TrendData = Record({
  data: undefined,
  // doesn't seem to be used for anything
  milestones: undefined,
});

function handleCancel(handler) {
  return error => {
    if (Api.isCancel(error)) {
      throw error;
    }
    handler(error);
  };
}

class RepProfilePageActions {
  constructor() {
    this.generateActions(
      'openCallDetails',
      'resetRepData',
      'selectTabIndex',
      'selectScoredByType',
      'clearTrendData',
      ...generateAsyncActions(
        'getSummary',
        'getCallActivity',
        'getCallDonut',
        'getCallTeamMetrics',
        'getCoachingActivity',
        'getCoachingTeamMetrics',
        'getCallDetails',
        'getTeamConversationTrend',
        'getTeamCallDurationTrend',
        'getCoachingPlans',
        'getTrendData',
        'getCoachingTrendData',
        'getCallMetrics',
        'getTopicPercentageTrend'
      )
    );
  }

  getSummary(userId, teamId, startDate, endDate) {
    return dispatch => {
      dispatch(userId, teamId, startDate, endDate);

      return RepProfileSource.getSummary(userId, startDate, endDate).then(
        this.getSummarySuccess,
        handleCancel(this.getSummaryError)
      );
    };
  }

  getCallActivity(team, userId, startDate, endDate) {
    return dispatch => {
      dispatch(team, userId, startDate, endDate);
      return RepProfileSource.getCallActivity(team, userId, startDate, endDate).then(
        this.getCallActivitySuccess,
        handleCancel(this.getCallActivityError)
      );
    };
  }

  getCallDonut(teamId, startDate, endDate) {
    return dispatch => {
      dispatch(teamId, startDate, endDate);
      return RepProfileSource.getCallDonut(teamId, startDate, endDate).then(
        this.getCallDonutSuccess,
        handleCancel(this.getCallDonutError)
      );
    };
  }

  getCallTeamMetrics(teamId, userId, startDate, endDate) {
    return dispatch => {
      dispatch(teamId, userId, startDate, endDate);
      return RepProfileSource.getCallTeamMetrics(teamId, userId, startDate, endDate).then(
        this.getCallTeamMetricsSuccess,
        handleCancel(this.getCallTeamMetricsError)
      );
    };
  }

  getTeamConversationTrend(teamId, startDate, endDate) {
    return dispatch => {
      dispatch(teamId, startDate, endDate);
      return RepProfileSource.getTeamConversationTrend(teamId, startDate, endDate).then(
        this.getTeamConversationTrendSuccess,
        handleCancel(this.getTeamConversationTrendError)
      );
    };
  }

  getTeamCallDurationTrend(teamId, startDate, endDate) {
    return dispatch => {
      dispatch(teamId, startDate, endDate);
      return RepProfileSource.getTeamCallDurationTrend(teamId, startDate, endDate).then(
        this.getTeamCallDurationTrendSuccess,
        handleCancel(this.getTeamCallDurationTrendError)
      );
    };
  }

  getCoachingActivity({direction, teamId, userId, startDate, endDate}) {
    return dispatch => {
      dispatch(direction, teamId, userId, startDate, endDate);
      return RepProfileSource.getCoachingActivity(
        direction,
        teamId,
        userId,
        startDate,
        endDate
      ).then(this.getCoachingActivitySuccess, handleCancel(this.getCoachingActivityError));
    };
  }

  getCoachingTeamMetrics({direction, teamId, userId, startDate, endDate}) {
    return dispatch => {
      dispatch(direction, teamId, userId, startDate, endDate);
      return RepProfileSource.getCoachingTeamMetrics(
        direction,
        teamId,
        userId,
        startDate,
        endDate
      ).then(this.getCoachingTeamMetricsSuccess, handleCancel(this.getCoachingTeamMetricsError));
    };
  }

  getCallMetrics(params) {
    return dispatch => {
      dispatch();
      return RepProfileSource.getCallMetrics(params).then(
        this.getCallMetricsSuccess,
        this.getCallMetricsError
      );
    };
  }

  getCoachingPlans(userId) {
    const twelveMonthsAgo = moment().subtract(12, 'months').startOf('day').format();
    return dispatch => {
      dispatch(userId);

      return CoachingPlanSource.fetchAll(
        {
          recipientId: userId,
          includeReviews: true,
          includeCompleted: true,
          createdAfter: twelveMonthsAgo,
        },
        handleCancel(error => this.getCoachingPlansError(userId, error))
      ).then(plans => {
        const velocities = DirectReportsSource.planVelocities(plans.map(p => p.id));

        return Promise.all([velocities]).then(
          ([velocities]) => {
            this.getCoachingPlansSuccess(userId, plans, velocities);
          },
          handleCancel(error => this.getCoachingPlansError(userId, error))
        );
      });
    };
  }

  getCallDetails(userId, date) {
    return dispatch => {
      dispatch([userId, date]);
      return RepProfileSource.getCallDetails(userId, date).then(
        ([details, fields]) => this.getCallDetailsSuccess([userId, date, details, fields]),
        handleCancel(error => this.getCallDetailsError([userId, date, error]))
      );
    };
  }

  getTrendData(params) {
    return dispatch => {
      dispatch([params]);
      return RepProfileSource.getTrendData(params).then(
        trendData => this.getTrendDataSuccess(new TrendData(trendData)),
        handleCancel(this.getTrendDataError)
      );
    };
  }

  // special case for topic call percentages
  getTopicPercentageTrend(params) {
    return dispatch => {
      dispatch([params]);
      return Promise.all([
        RepProfileSource.getTrendData({...params, type: MetricType.trigger}),
        RepProfileSource.getTrendData({
          ...params,
          type: MetricType.conversations,
          context: null,
        }),
      ])
        .then(([topicCalls, totalCalls]) =>
          this.getTopicPercentageTrendSuccess([
            new TrendData(topicCalls),
            new TrendData(totalCalls),
          ])
        )
        .catch(handleCancel(this.getTrendDataError));
    };
  }
}

export default alt.createActions(RepProfilePageActions);
